'use client';
import { useMediaQuery } from '@mnd-frontend/hooks';
import { Box, Button, Icons } from '@mnd-frontend/ui/publish/components';
import { media } from '@mnd-frontend/ui/publish/utils';
import { GREY_200, GREY_300, GREY_800, RED_700 } from '@mnd-frontend/ui/shared/tokens';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import { useReportStaticData, useReportTranslation } from '../../hooks';
import { AdminPanelLink } from './AdminPanelLink';

const Wrapper = styled.div`
  display: flex;
  height: 75vh;
`;

const Sidebar = styled.div`
  width: 20rem;
  padding: 2rem 1rem;
  background-color: ${GREY_200};

  ul {
    list-style-type: none;
    padding: 0;
    margin: 0;

    li {
      padding: 1rem;
      border-radius: 0.5rem;
      cursor: pointer;

      &:hover {
        color: ${RED_700};
        background-color: ${GREY_300};
      }
    }
  }
`;

const MobileSidebar = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  left: ${({ $isVisible }) => ($isVisible ? '0' : '-16rem')};
  top: 0;
  bottom: 0;
  width: 14.5rem;
  padding: 2rem 1rem;
  background-color: ${GREY_200};
  transition: left 0.3s ease-in-out;
  z-index: 1000;
`;

const SidebarList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const sidebarListElementActiveStyle = css`
  color: ${RED_700};
  background-color: ${GREY_300};
`;

const SidebarListElement = styled.li<{ $active: boolean }>`
  color: ${GREY_800};
  padding: 1rem;
  border-radius: 0.5rem;
  cursor: pointer;

  &:hover {
    ${sidebarListElementActiveStyle}
  }
  ${({ $active }) => $active && sidebarListElementActiveStyle}
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  padding: 2rem;
  overflow: hidden;
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  button {
    margin-left: auto;
  }
`;

const Title = styled.h2`
  margin-bottom: 1rem;
`;

const Scrollable = styled.div`
  flex: 1;
  overflow-y: auto;
`;

type SidebarItem = {
  id: string;
  label: string;
};

type ViewItem = {
  id: string;
  view: React.ReactNode;
};

type Props = {
  sidebar: SidebarItem[];
  views: ViewItem[];
  close: () => void;
};

export const SidebarModal = ({ sidebar, views, close }: Props) => {
  const { t } = useReportTranslation();
  const { pressroomId } = useReportStaticData();
  const [selectedId, setSelectedId] = useState(sidebar[0].id);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const isMobile = useMediaQuery(media.lessThan('phablet'));

  const getViewTitle = () => {
    const selectedItem = sidebar.find(item => item.id === selectedId);
    return selectedItem ? selectedItem.label : '';
  };

  const renderView = () => {
    const selectedView = views.find(view => view.id === selectedId);
    return selectedView ? selectedView.view : null;
  };

  return (
    <Box openedAsModal size="lg" bodyPadding={false}>
      <Wrapper>
        {!isMobile && (
          <Sidebar>
            <SidebarList>
              {sidebar.map(item => (
                <SidebarListElement
                  key={item.id}
                  $active={selectedId === item.id}
                  onClick={() => setSelectedId(item.id)}
                >
                  {item.label}
                </SidebarListElement>
              ))}
              <AdminPanelLink pressroomId={pressroomId} />
            </SidebarList>
          </Sidebar>
        )}
        <Content>
          <ContentHeader>
            {isMobile && (
              <div>
                <Button
                  type="button"
                  configuration="text"
                  aria-label={t('reports_menu_toggle')}
                  onClick={() => setMobileSidebar(!mobileSidebar)}
                  icon={<Icons.Menu />}
                />
                <MobileSidebar $isVisible={mobileSidebar}>
                  <SidebarList>
                    {sidebar.map(item => (
                      <SidebarListElement
                        key={item.id}
                        $active={selectedId === item.id}
                        onClick={() => {
                          setSelectedId(item.id);
                          setMobileSidebar(!mobileSidebar);
                        }}
                      >
                        {item.label}
                      </SidebarListElement>
                    ))}
                    <AdminPanelLink pressroomId={pressroomId} />
                  </SidebarList>
                </MobileSidebar>
              </div>
            )}
            <Title>{getViewTitle()}</Title>
            <Button
              type="button"
              configuration="text"
              aria-label={t('common_close')}
              icon={<Icons.Close />}
              onClick={close}
            />
          </ContentHeader>
          <Scrollable>{renderView()}</Scrollable>
        </Content>
      </Wrapper>
    </Box>
  );
};
